import { useQuery } from '@tanstack/react-query';
import {
  getGamePicks,
  getPicksOfTheDay,
  getParlayOfTheDay,
  getMlbTotalBasesPredictions,
  getMlbHrPredictions,
  getMlbHitPredictions,
  getMlbTwoHitsPredictions,
  getPicksLedger,
  getSportsbooks,
} from '../services/api';

const STALE_2_MIN = 2 * 60 * 1000;
const STALE_10_MIN = 10 * 60 * 1000;

export function useGamePicks(dateStr) {
  return useQuery({
    queryKey: ['gamePicks', dateStr],
    queryFn: () => getGamePicks(dateStr),
    enabled: !!dateStr,
    staleTime: STALE_2_MIN,
  });
}

export function usePicksOfTheDay(dateStr) {
  return useQuery({
    queryKey: ['picksOfTheDay', dateStr],
    queryFn: () => getPicksOfTheDay(dateStr),
    enabled: !!dateStr,
    staleTime: STALE_2_MIN,
  });
}

export function useParlayOfTheDay(dateStr) {
  return useQuery({
    queryKey: ['parlayOfTheDay', dateStr],
    queryFn: () => getParlayOfTheDay(dateStr),
    enabled: !!dateStr,
    staleTime: STALE_2_MIN,
  });
}

/**
 * @param {string | null | undefined} date
 * @param {boolean} activeOnly
 * @param {string | null | undefined} sportsbook
 * @param {string | null | undefined} model
 * @param {{ enabled?: boolean }} [queryOptions] - Set `enabled: false` to skip fetch (e.g. Team Picks before a team is chosen).
 */
export function useTotalBasesPredictions(date, activeOnly, sportsbook, model, queryOptions = {}) {
  const enabled = (queryOptions.enabled ?? true) && !!date;
  return useQuery({
    queryKey: ['totalBases', date, activeOnly, sportsbook, model],
    queryFn: () => getMlbTotalBasesPredictions(date, activeOnly, sportsbook, model),
    staleTime: STALE_2_MIN,
    enabled,
  });
}

/**
 * @param {{ enabled?: boolean }} [queryOptions]
 */
export function useHrPredictions(date, activeOnly, sportsbook, queryOptions = {}) {
  const enabled = (queryOptions.enabled ?? true) && !!date;
  return useQuery({
    queryKey: ['hrPredictions', date, activeOnly, sportsbook],
    queryFn: () => getMlbHrPredictions(date, activeOnly, sportsbook),
    staleTime: STALE_2_MIN,
    enabled,
  });
}

/**
 * @param {{ enabled?: boolean }} [queryOptions]
 */
export function useHitPredictions(date, activeOnly, sportsbook, queryOptions = {}) {
  const enabled = (queryOptions.enabled ?? true) && !!date;
  return useQuery({
    queryKey: ['hitPredictions', date, activeOnly, sportsbook],
    queryFn: () => getMlbHitPredictions(date, activeOnly, sportsbook),
    staleTime: STALE_2_MIN,
    enabled,
  });
}

/**
 * @param {{ enabled?: boolean }} [queryOptions]
 */
export function useTwoHitsPredictions(date, activeOnly, sportsbook, queryOptions = {}) {
  const enabled = (queryOptions.enabled ?? true) && !!date;
  return useQuery({
    queryKey: ['twoHitsPredictions', date, activeOnly, sportsbook],
    queryFn: () => getMlbTwoHitsPredictions(date, activeOnly, sportsbook),
    staleTime: STALE_2_MIN,
    enabled,
  });
}

export function usePicksLedger() {
  return useQuery({
    queryKey: ['picksLedger'],
    queryFn: getPicksLedger,
    staleTime: STALE_10_MIN,
  });
}

/** @deprecated Use usePicksLedger */
export function useModelStats() {
  return usePicksLedger();
}

export function useSportsbooks() {
  return useQuery({
    queryKey: ['sportsbooks'],
    queryFn: getSportsbooks,
    staleTime: STALE_10_MIN,
  });
}
